// AI Generated Test
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { PagePerformanceVersionService } from '@client/app/modules/page-performance/services/page-performance-version/page-performance-version.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'changes-history-dashboard-table-performance',
    standalone: false,
    template: `
        <div class="row change-history-tables-performance">
            <div class="col-48">
                <ng-container  *ngFor="let version of versions; trackBy: trackByIndex">
                    <table class="table table-hover table-fixed">
                        <thead>
                        <tr>
                            <th class="col-8 col-lg-7">
                                {{'Scanned at' | translate}}
                            </th>
                            <th class="text-right col-4 col-lg-8">
                                <span class="visible-print-block">{{'Pages' | translate}}</span>
                                <span class="hidden-print">{{'Performance Pages' | translate}}</span>
                            </th>
                            <th class="text-right col-4 col-lg-8">{{'Avg. Performance Score' | translate}}</th>
                            <th class="text-right col-4 col-lg-8" *ngFor="let metric of version.avgMetrics">{{ metric.label | translate}}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr *ngFor="let story of formattedSeriesByVersion[version.id]">
                            <td class="text-v-middle">
                                <user-date [twoLines]="true"
                                           [date]="story.post_processing_done_at || story.date">
                                </user-date>
                            </td>
                            <td class="text-v-middle text-right">
                                {{story.performance_page_count | monNumber}}
                            </td>
                            <td class="text-v-middle text-right">
                                <page-performance-score-doughnut-chart [score]="story.performance_score_average * 100 | monNumber">
                                </page-performance-score-doughnut-chart>
                            </td>
                            <td class="text-v-middle text-right" *ngFor="let metric of version.avgMetrics">
                                <span>{{ story.formattedFieldValue[metric.id] }}</span>
                                <span [ngClass]="story.changesColorClass[metric.id]"
                                      *ngIf="story.changePercentageValue[metric.id] !== 0">
                                    (<span [ngClass]="story.changeIconClass[metric.id]">
                                    </span>
                                    {{ story.changePercentageValue[metric.id] / 100 | monNumber:'0.[00]%' }})
                                </span>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <div class="performance-update-indicator" *ngIf="shouldDisplayUpdateIndicator[versions.indexOf(version)]">
                        <a class="performance-update-indicator-link" [attr.href]="version.link" target="_blank">{{ version.label }}</a>
                    </div>
                </ng-container>
            </div>
        </div>
    `,
})
export class PerformanceComponent implements OnInit, OnChanges {
    @Input() history: any;
    @Input() domain: any;
    @Input() domainGroup: any;

    fields: any;
    scoreFields: any;
    decimalsFields: any;
    versions: any[] = [];
    seriesByVersion: any = {};
    formattedSeriesByVersion: any = {};
    shouldDisplayUpdateIndicator: any[] = [];

    constructor (
        private pipeService: PipeService,
        private historyHelperService: HistoryHelperService,
        private pagePerformanceVersionService: PagePerformanceVersionService,
    ) {}

    ngOnInit (): void {
        this.fields = [
            'performance_first_contentful_paint_average',
            'performance_largest_contentful_paint_average',
            'performance_time_to_interactive_average',
            'performance_speed_index_average',
            'performance_total_blocking_time_average',
            'performance_cumulative_layout_shift_average',
        ];
        this.scoreFields = ['performance_score_average'];
        this.decimalsFields = ['performance_cumulative_layout_shift_average'];
        this.versions = [];
        this.seriesByVersion = {};
        this.shouldDisplayUpdateIndicator = [];
        this.computeHistory();
    }

    ngOnChanges (): void {
        if (!this.scoreFields) {
            return;
        }
        this.computeHistory();
    }

    trackByIndex (index: number, item: any): number {
        return index;
    }

    private computeHistory (): void {
        const performanceHistory = this.history.filter((item: any) => Boolean(item['performance_version']));
        const seriesByVersion = this.pagePerformanceVersionService.getSeriesByVersion(performanceHistory, 'performance_version');
        this.versions = seriesByVersion.versions;
        this.seriesByVersion = seriesByVersion.series;
        this.shouldDisplayUpdateIndicator = [];
        const versions = safeClone(this.versions);
        versions.reverse();
        for (let i = 0; i < versions.length; i++) {
            const version = versions[i];
            if (!version) {
                continue;
            }

            const originalSeries = safeClone(this.seriesByVersion[version.id]);
            const currentSeries = this.seriesByVersion[version.id];
            this.formattedSeriesByVersion[version.id] = safeClone(this.seriesByVersion[version.id]);

            this.formattedSeriesByVersion[version.id] = currentSeries.map((entry: any) => {
                const computedEntry = safeClone(entry);
                computedEntry.changesColorClass = {};
                computedEntry.changePercentageValue = {};
                computedEntry.changeIconClass = {};
                computedEntry.formattedFieldValue = {};

                version.avgMetrics.forEach((metric: any) => {
                    const metricId = metric.id;
                    computedEntry.formattedFieldValue[metricId] = this.formatFieldValue(entry[metricId], metricId);
                    computedEntry.changesColorClass[metricId] = this.changesColorClass(entry, metricId, originalSeries);
                    computedEntry.changePercentageValue[metricId] = this.getChangePercentageValue(entry.id, metricId, originalSeries);
                    computedEntry.changeIconClass[metricId] = this.changesIconClass(entry, metricId, originalSeries);
                });

                return computedEntry;
            }).sort((a: any, b: any) => {
                return b.id - a.id;
            });

            const previousVersion = versions[i - 1];
            if (!previousVersion) {
                this.shouldDisplayUpdateIndicator.push(false);
            } else {
                const previousVersionIsTransitionVersion = previousVersion.id.indexOf('-') > -1;
                if (previousVersionIsTransitionVersion) {
                    const transitionVersions = previousVersion.id.split('-');
                    if (transitionVersions[1] === version.id) {
                        this.shouldDisplayUpdateIndicator.push(false);
                        return;
                    }
                }
                this.shouldDisplayUpdateIndicator.push(true);
            }
        }
        this.shouldDisplayUpdateIndicator.reverse();
    }

    private getChangeValue (id: any, field: any, series: any): any {
        return this.historyHelperService.getChangeValue(id, series, [field]);
    }

    private getChangePercentageValue (id: any, field: any, series: any): any {
        return this.historyHelperService.getChangePercentageValue(id, series, [field]);
    }

    private changesColorClass (story: any, field: any, series: any): any {
        let value = this.getChangeValue(story.id, field, series);

        if (this.scoreFields.indexOf(field) !== -1) {
            value = -value;
        }

        return this.historyHelperService.getChangeColorClass(value);
    }

    private changesIconClass (story: any, field: any, series: any): any {
        let value = this.getChangeValue(story.id, field, series);

        if (this.scoreFields.indexOf(field) === -1) {
            value = -value;
        }

        return this.historyHelperService.getChangeIconClass(value);
    }

    private formatFieldValue (value: any, key: any): any {
        if (!value || !this.scoreFields) {
            return 0;
        }
        if (this.scoreFields.indexOf(key) !== -1) {
            return this.pipeService.get('monNumber')(value * 100, '0.00');
        } else if (this.decimalsFields.indexOf(key) !== -1) {
            return this.pipeService.get('monNumber')(value, '0.00');
        } else {
            return this.pipeService.get('monNumber')(value) + ' ms';
        }
    }
}
